import axios from 'axios';
import config from '../utils/config';
import parseError from '../utils/parseError';
import { clearAccessToken, getAccessToken, setAccessToken } from './accessToken';
import NavigationService from './navigation.service';
import { PORTAL_ROUTE } from '../constants/routes';

// Backend 403s tagged with one of these `reason` values mean "there's nothing for you here" -
// either the caller has no active customer at all (SuspendedUserFilter), or they're not
// permitted to access this specific object (CustomerAccessibilityFilter,
// EnvAccessibilityFilter, ResourceCustomerEnvAccessFilter, ApplicationAccessFilter). An
// untagged 403 (or any other status) is left alone so an unrelated failure doesn't yank
// someone away from a page they're legitimately using.
const REDIRECT_TO_COMPANIES_REASONS = new Set(['no_active_customer', 'forbidden_object']);

// Tagged on 403s from endpoints that require an active company subscription - environment access
// (EnvAccessibilityFilter, ResourceCustomerEnvAccessFilter) and company user/invitation management
// (ActiveSubscriptionFilter). Company name/subscription viewing itself is never gated this way.
// Unlike the reasons above, this sends the caller to a dedicated "contact us to renew" page
// instead of /companies.
const SUBSCRIPTION_EXPIRED_REASON = 'subscription_expired';

const instance = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// AuthProvider provides a callback when the session ends from middlware
let endSession = () => {};
export const setSessionExpiredHandler = handler => {
  endSession = handler;
};

let refreshPromise = null;

const withRefreshLock = request =>
  navigator.locks ? navigator.locks.request('auth-refresh', request) : request();

// Refresh is always done here in the API to provide the lock and prevent duplicate refreshes.
export const refreshAccessToken = () => {
  refreshPromise ??= withRefreshLock(() => instance.post('/auth/refresh'))
    .then(response => {
      setAccessToken(response.accessToken);
      return response;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

instance.interceptors.request.use(
  requestConfig => {
    const accessToken = getAccessToken();

    if (accessToken) {
      requestConfig.headers['Authorization'] = 'Bearer ' + accessToken;
    }

    return requestConfig;
  },
  error => Promise.reject(error),
);

instance.interceptors.response.use(
  res => res.data,
  async err => {
    const originalConfig = err.config;
    const isAuthRequest = originalConfig.url.startsWith('/auth/');

    // Auth requests (login, refresh, etc.) skip the redirect/refresh-retry handling below - a
    // failed login shouldn't trigger a token refresh attempt, for instance - but they still fall
    // through to the plain rejection at the bottom so a failure (e.g. wrong password) always
    // propagates as a rejected promise instead of being silently swallowed.
    if (!isAuthRequest && err.response) {
      // A suspended (or customer-less) user's token is still valid, so they can navigate the
      // SPA client-side even though every real request 403s - send them somewhere that
      // actually works instead of leaving whatever page they were on broken. Scoped to this
      // one backend-tagged reason so an unrelated 403 (e.g. a cross-tenant access attempt)
      // doesn't redirect someone away from a page they were legitimately using.
      if (
        err.response.status === 403 &&
        REDIRECT_TO_COMPANIES_REASONS.has(err.response.data?.reason)
      ) {
        NavigationService.navigate(`${PORTAL_ROUTE}/companies`);
        return Promise.reject(parseError(err));
      }

      if (err.response.status === 403 && err.response.data?.reason === SUBSCRIPTION_EXPIRED_REASON) {
        NavigationService.navigate(`${PORTAL_ROUTE}/account-expired`);
        return Promise.reject(parseError(err));
      }

      // Access Token was expired but is needed for this call
      // attempt to refresh
      if (err.response.status === 401 && !originalConfig._retry) {
        originalConfig._retry = true;
        try {
          await refreshAccessToken();
        } catch {
          clearAccessToken();
          endSession();
          return Promise.reject(parseError(err));
        }

        return instance(originalConfig);
      }
    }

    return Promise.reject(parseError(err));
  },
);

export default instance;
